
import React, { useState } from 'react';
import { Pencil, Info, Check, Plus, Save, X, DollarSign, Zap } from 'lucide-react';
import { Plan } from './types';
import { Card } from './SharedComponents';

export const SettingsPlans = ({ plans, isSuperAdmin, onUpdatePlan }: { plans: Plan[], isSuperAdmin: boolean, onUpdatePlan: (p: Plan) => void }) => {
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

  return (
    <div className="space-y-12 animate-in slide-in-from-bottom-4 duration-500">
      <div className="text-center space-y-2">
        <h2 className={`text-5xl font-black uppercase tracking-tighter ${isSuperAdmin ? 'text-white' : 'text-secondary'}`}>GESTÃO COMERCIAL</h2>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Definição de Ofertas e Preços da Plataforma</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan) => (
          <Card key={plan.id} className={`relative p-10 border-2 transition-all flex flex-col items-center text-center rounded-[40px] ${plan.popular ? 'border-primary shadow-2xl scale-105 z-10 bg-white' : 'border-gray-100 hover:border-gray-200 bg-white'}`}>
            {plan.popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-lg whitespace-nowrap">
                Mais Popular
              </div>
            )}

            {isSuperAdmin && (
              <button 
                onClick={() => setEditingPlan(plan)}
                className="absolute top-4 right-4 p-3 bg-gray-50 text-gray-400 hover:text-primary rounded-2xl transition-all shadow-sm"
                title="Editar Plano"
              >
                <Pencil size={18} />
              </button>
            )}
            
            <div className="mb-10 w-full">
              <h4 className="text-2xl font-black text-secondary uppercase tracking-tight mb-1">{plan.name}</h4>
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-8">{plan.subtitle}</p>
              
              <div className="flex items-start justify-center gap-1">
                <span className="text-sm font-bold text-gray-400 mt-2">R$</span>
                <span className="text-6xl font-black text-secondary tracking-tighter">{plan.price}</span>
                <span className="text-sm font-bold text-gray-400 mt-8">/mês</span>
              </div>
              
              <div className="mt-4 inline-flex items-center gap-1.5 px-4 py-1.5 bg-gray-50 rounded-full border border-gray-100">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">+ R$ {plan.setup} TAXA SETUP ÚNICA</span>
              </div>
            </div>

            <div className="space-y-4 flex-1 mb-10 w-full text-left">
              {plan.features.map(f => (
                <div key={f} className="flex items-center gap-4">
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center border ${plan.popular ? 'border-primary text-primary' : 'border-gray-200 text-gray-300'}`}>
                    <Check size={12} strokeWidth={4} />
                  </div>
                  <span className="text-xs font-black text-gray-500 uppercase tracking-tight">{f}</span>
                </div>
              ))}
            </div>

            <button className={`w-full py-5 rounded-[24px] font-black uppercase text-[11px] tracking-widest transition-all active:scale-95 hover:brightness-110 ${plan.btnColor}`}>
              Assinar Agora
            </button>
          </Card>
        ))}
      </div>

      {editingPlan && (
        <EditPlanModal 
          plan={editingPlan} 
          onClose={() => setEditingPlan(null)} 
          onSubmit={(p) => { 
            onUpdatePlan(p); 
            setEditingPlan(null); 
          }} 
        />
      )}
    </div>
  );
};

const EditPlanModal = ({ plan, onClose, onSubmit }: { plan: Plan, onClose: () => void, onSubmit: (p: Plan) => void }) => {
  const [formData, setFormData] = useState({ 
    ...plan, 
    featuresString: plan.features.join('\n') 
  });

  const handlePriceChange = (field: 'price' | 'setup', value: string) => {
    setFormData(prev => ({
        ...prev,
        [field]: value === '' ? 0 : Number(value)
    }));
  };

  const handleFinalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalFeatures = formData.featuresString
        .split('\n')
        .map(f => f.trim())
        .filter(f => f !== '');
        
    const finalPlan: Plan = {
        ...formData,
        features: finalFeatures,
        // Garantir que os campos numéricos são números reais
        price: Number(formData.price),
        setup: Number(formData.setup)
    };
    
    onSubmit(finalPlan);
  };

  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[150] flex items-center justify-center p-6 text-gray-900">
      <div className="bg-white rounded-[48px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8 bg-secondary text-white flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tight">Editar {plan.name}</h2>
            <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Oferta SaaS Master</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl"><X size={24} /></button>
        </div>

        <form onSubmit={handleFinalSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
             <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Slogan de Venda</label>
                <input 
                    className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary focus:border-primary transition-all" 
                    value={formData.subtitle} 
                    onChange={e => setFormData({...formData, subtitle: e.target.value})} 
                />
             </div>
             <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Valor Mensal (R$)</label>
                  <input 
                    type="number" 
                    className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-black text-secondary focus:border-primary transition-all" 
                    value={formData.price} 
                    onChange={e => handlePriceChange('price', e.target.value)} 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Taxa Setup (R$)</label>
                  <input 
                    type="number" 
                    className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-black text-secondary focus:border-primary transition-all" 
                    value={formData.setup} 
                    onChange={e => handlePriceChange('setup', e.target.value)} 
                  />
                </div>
             </div>
             <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Benefícios (Um por linha)</label>
                <textarea 
                    rows={5} 
                    className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-xs text-secondary resize-none focus:border-primary transition-all" 
                    value={formData.featuresString} 
                    onChange={e => setFormData({...formData, featuresString: e.target.value})} 
                />
             </div>
          </div>
          <div className="pt-4 flex gap-4">
             <button type="button" onClick={onClose} className="flex-1 py-4 border-2 border-gray-100 text-gray-400 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-50 transition-all active:scale-95">Cancelar</button>
             <button type="submit" className="flex-1 py-4 bg-primary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2">
                <Save size={18} /> Salvar Alterações
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};
